'use client'

import React, { memo, useRef } from 'react'
import { Box, Fade, Typography, useTheme } from '@mui/material'
import useOnScreen from '@hooks/useOnScreen'
import { useTranslation } from 'react-i18next'
import Checkmark from '@assets/checkmark.svg'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { DewiColorsGreen5, DewiColorsStatesSuccess } from '@furry/tokens'

export type RoadmapItemProps = {
  title: string
  subtitle: string
  date: string
  isLastItem: boolean
}

function Roadmap() {
  const theme = useTheme()
  const root = useRef()
  const isVisible = useOnScreen(root)
  const { t } = useTranslation()

  const roadmapItems: {
    title: string
    subtitle: string
    date: string
    completed?: boolean
  }[] = Array.from(
    t('roadmapPage.items', {
      returnObjects: true,
    }),
  ).map((item: any, index: number) => ({
    ...item,
    completed: index < 4,
  }))

  return (
    <Box className="bg-dark-9">
      <Fade in={isVisible} timeout={2000} ref={root}>
        <Box padding={5} className="bg-dark-9">
          <Typography
            align="center"
            variant="h2"
            marginBottom={theme.spacing(4)}
          >
            {t('roadmapPage.title')}
          </Typography>
          <RoadmapSwiper items={roadmapItems} />
        </Box>
      </Fade>
    </Box>
  )
}

function RoadmapSwiper({
  items,
}: {
  items: {
    title: string
    subtitle: string
    date: string
    completed?: boolean
  }[]
}) {
  return (
    <Swiper
      className="relative"
      scrollbar={{
        hide: true,
      }}
      style={{ paddingLeft: '1rem', paddingRight: '1rem', overflow: 'scroll' }}
      spaceBetween={25}
      breakpoints={{
        // when window width is >= 320px
        350: {
          slidesPerView: 1,
          spaceBetween: 20,
        },
        // when window width is >= 480px
        550: {
          slidesPerView: 2,
          spaceBetween: 30,
        },
        // when window width is >= 640px
        750: {
          slidesPerView: 3,
          spaceBetween: 40,
        },
        950: {
          slidesPerView: 4,
          spaceBetween: 40,
        },
      }}
    >
      <div className="absolute top-0 left-0 w-5 h-full z-10 bg-gradient-to-r from-dark-9" />
      <div className="absolute top-0 right-0 w-5 h-full z-10 bg-gradient-to-l from-dark-9" />
      {items.map((item, index) => (
        <SwiperSlide className="cursor-grab" key={index}>
          <div className="relative h-full">
            <div className="relative flex items-center justify-center mb-5">
              <div
                className={`${
                  index === items.length - 1 ? 'right-0' : 'left-0'
                } h-px absolute top-2 w-[125%] z-0 ${item.completed ? 'bg-green-500' : 'from-purple-700 bg-gradient-to-l to-green-500'}`}
              ></div>
              <div
                className={`${
                  index === items.length - 1 ? 'right-0' : 'left-0'
                } absolute top-[3px] h-3 w-3 z-0 shadow-roadmap ${item.completed ? 'shadow-green-500' : 'shadow-purple-700'} ${item.completed ? 'bg-green-500' : 'bg-purple-700'} rounded-full`}
              ></div>
              <div className="text-sm text-gray-6 px-4 relative bg-dark-9">
                {item.date}
              </div>
            </div>
            <div className="bg-dark-8 overflow-hidden rounded-card">
              <div className="text-lg text-gray-0 font-bold font-display uppercase bg-dark-9 px-3 py-2 flex flex-row items-center gap-2">
                {item.title}
                {item.completed && (
                  <Checkmark color={DewiColorsGreen5} width={20} height={20} />
                )}
              </div>
              <div className="text-base text-gray-6 py-2 px-3">
                {item.subtitle}
              </div>
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

export default memo(Roadmap)
